import { CountryCode } from "libphonenumber-js";
import { FC, useEffect } from "react";
import { useI18n } from "../../hooks/useTranslations";
import { Supplier } from "../../http/suppliers/types";
import { isValidEmail } from "../../types/utils";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import Input from "../ui/Input";
import PhoneNumberInput from "../ui/PhoneNumberInput";

export interface SupplierFormBody {
  name: string;
  address?: string;
  email?: string;
  phoneNumber: string | null;
  countryPhone: CountryCode | null;
}

type SupplierFormProps = {
  supplier?: Supplier | null;
  onSubmit: (form: SupplierFormBody) => Promise<void>;
  isDisabled?: boolean;
  isLoading?: boolean;
};

const SupplierForm: FC<SupplierFormProps> = ({ supplier, onSubmit, isDisabled, isLoading }: SupplierFormProps) => {
  const i18n = useI18n();

  const defaultValues = {
    name: supplier?.name,
    address: supplier?.address,
    email: supplier?.email,
    phoneNumber: supplier?.phoneNumber,
  };

  return (
    <Form<SupplierFormBody>
      defaultValues={defaultValues}
      submitText={i18n.t.common.save}
      disabled={isDisabled}
      onSubmit={() => onSubmit}
      itemCreationInfo={supplier}
    >
      {({ register, reset, formState: { errors }, setError, clearErrors, setValue }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [supplier]);

        return (
          <FormFieldsContainer>
            <FormField label={i18n.t.common.name} error={errors.name}>
              {({ labelId, isInvalid }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={isDisabled || isLoading}
                  {...register("name", {
                    required: true,
                    minLength: 1,
                  })}
                />
              )}
            </FormField>
            <FormField label={i18n.t.common.address} error={errors.address} isOptional>
              {({ labelId, isInvalid }) => (
                <Input id={labelId} isInvalid={isInvalid} disabled={isDisabled || isLoading} {...register("address")} />
              )}
            </FormField>
            <FormField label={i18n.t.common.email} error={errors.email} isOptional>
              {({ labelId, isInvalid }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={isDisabled || isLoading}
                  {...register("email", { validate: (value) => !value || isValidEmail(value) })}
                />
              )}
            </FormField>
            <FormField label={i18n.t.common.phoneNumber} error={errors.phoneNumber} isOptional>
              {({ labelId }) => {
                return (
                  <PhoneNumberInput
                    labelId={labelId}
                    isDisabled={isDisabled}
                    isLoading={isLoading}
                    onSetPhoneNumber={(v) => setValue("phoneNumber", v)}
                    onSetCountryPhone={(v) => setValue("countryPhone", v)}
                    phoneNumber={supplier?.phoneNumber}
                    onError={(error) =>
                      error ? setError("phoneNumber", { message: error }) : clearErrors("phoneNumber")
                    }
                  />
                );
              }}
            </FormField>
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default SupplierForm;
